import { useRef } from 'react';

import { isBrowser } from 'lib/utils';

import useIsomorphicLayoutEffect from './useIsomorphicLayoutEffect';

const getScrollPosition = ({ element, useWindow }) => {
  if (!isBrowser()) return { x: 0, y: 0 };

  const target = element ? element.current : document.body;
  const position = target.getBoundingClientRect();

  return useWindow
    ? { x: window.scrollX, y: window.scrollY }
    : { x: position.left, y: position.top };
};

const useScrollPosition = (
  effect,
  {
    deps = [],
    enabled = true,
    element = null,
    useWindow = true,
    wait = 800,
  } = {
    deps: [],
    enabled: true,
    element: null,
    useWindow: true,
    wait: 800,
  }
) => {
  const position = useRef(getScrollPosition({ useWindow }));
  let throttleTimeout = null;

  const callBack = () => {
    const currPos = getScrollPosition({ element, useWindow });

    effect({ currPos, prevPos: position.current });

    position.current = currPos;
    throttleTimeout = null;
  };

  useIsomorphicLayoutEffect(() => {
    if (!enabled) {
      return;
    }

    const handleScroll = () => {
      if (wait) {
        if (throttleTimeout === null) {
          throttleTimeout = setTimeout(callBack, wait);
        }
      } else {
        callBack();
      }
    };

    window.addEventListener('scroll', handleScroll);

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [enabled, ...deps]);
};

export default useScrollPosition;
